import { useApolloClient } from '@apollo/client';
import { MaterialType, type TagSortOrder } from '@mnd-frontend/graphql-schema';
import { Checkbox, Dialog, Input, Select, textStyles } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { media } from '@mnd-frontend/ui/publish/utils';
import {
  GREY_400,
  NEUTRAL_WHITE,
  SPACING_0_5,
  SPACING_1,
  SPACING_1_5,
  SPACING_2,
  SPACING_3,
} from '@mnd-frontend/ui/shared/tokens';
import { MATERIAL_TYPE_LABELS, purgeImageUsages } from '@mnd-frontend/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MaterialIcon } from '../../components/MaterialIcon';
import { useTagQuery, useUntagMutation, useUpdateTagMutation } from '../../generated/hooks';
import type { TagManagementMaterialFragment } from '../../generated/types';

const Header = styled.div`
  background: ${NEUTRAL_WHITE};
  margin: 4px 0 ${SPACING_2} 0;
`;

const TagNameInputWrapper = styled.div`
  margin-bottom: ${SPACING_3};
  ${media.lessThan('phablet')} {
    margin-bottom: ${SPACING_1_5};
  }
`;

const MaterialHeader = styled.span<{ $checked: boolean }>`
  text-decoration: ${props => (props.$checked ? 'none' : 'line-through')};
  display: grid;
  grid-template-columns: ${({ theme }) => theme.sizingIconXxs} 1fr;
  align-items: center;
  gap: ${SPACING_1};
`;
const Center = styled.div`
  display: flex;
  align-items: center;
`;

const FlexItem = styled.div`
  border-top: 1px solid ${GREY_400};
  display: flex;
  justify-content: space-between;
  padding: ${SPACING_1};
  &:last-child {
    border-bottom: 1px solid ${GREY_400};
  }
`;

const InputWrapper = styled.div`
  margin-top: ${SPACING_1};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACING_2};

  ${media.lessThan('phablet')} {
    grid-template-columns: 1fr;
  }
`;

const StyledLabel = styled.label`
  ${textStyles.body}
  font-weight: 600;
  margin-bottom: ${SPACING_0_5};
`;

const EditTagModal = ({
  onClose,
  id,
  pressroomId,
  query,
  perPage,
  page,
  sortOrder,
}: {
  onClose: () => void;
  id: string;
  pressroomId: string;
  query: string;
  perPage: number;
  page: number;
  sortOrder: TagSortOrder;
}) => {
  const [t] = useTranslation();
  const { data, error } = useTagQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      pressroomId,
      id,
    },
  });

  const { showFailureToast } = useToasts();
  const tag = data?.pressroom?.tag;
  const [selectedFilter, setSelectedFilter] = useState<MaterialType | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [tagNameError, setTagNameError] = useState<string | undefined | null>(undefined);
  const [materials, setMaterials] = useState<TagManagementMaterialFragment[]>([]);
  const cache = useApolloClient().cache;

  const [updateTag] = useUpdateTagMutation({
    onCompleted: response => {
      purgeImageUsages(cache, response.tagUpdate?.newImage);
      purgeImageUsages(cache, response.tagUpdate?.previousImage);
    },
  });
  const [untag] = useUntagMutation();

  useEffect(() => {
    if (error) {
      showFailureToast(t('common_general_error'));
      onClose();
    }
  }, [error, onClose, showFailureToast, t]);

  const filterItem = (value: MaterialType | null) => ({
    label: (value
      ? t(MATERIAL_TYPE_LABELS[value])
      : t('category_management_edit_category_all_material')) as string,
    value,
  });

  const filters = [
    undefined,
    ...(tag?.materials ?? [])
      .map(material => MaterialType[material.__typename])
      .filter((value, index, self) => self.indexOf(value) === index), // uniq
  ];

  useEffect(() => {
    if (!tag) {
      return;
    }
    setMaterials(tag.materials);
    setSelectedIds(tag.materials.map(x => x.id));
  }, [tag]);

  useEffect(() => {
    if (!tag) {
      return;
    }

    const materials = (
      selectedFilter === null
        ? tag.materials
        : tag.materials.filter(material => material.__typename === selectedFilter)
    ).filter(material => {
      if (!material.header) {
        return true;
      }
      return material.header?.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase());
    });

    setMaterials(materials);
    setSelectedIds(materials.map(material => material.id));
  }, [tag, selectedFilter, searchQuery]);

  useEffect(() => {
    if (tag) {
      setName(tag.name);
    }
  }, [tag]);

  return (
    <Dialog
      description={t('category_management_edit_category_heading')}
      open={!!tag}
      confirmAction={{
        onClick: async () => {
          if (tag?.name !== name) {
            try {
              const result = await updateTag({
                variables: {
                  id,
                  name,
                  query,
                  perPage,
                  page,
                  sortOrder,
                  pressroomId,
                },
              });
              const error = result.data?.tagUpdate?.error;
              if (error) {
                if (error.__typename === 'TagUpdateValidationError') {
                  setTagNameError(error.name);
                } else {
                  showFailureToast(error?.message ?? t('common_general_error'));
                }
                return;
              }
            } catch (_err) {
              showFailureToast(t('common_general_error'));
              return;
            }
          }
          const removedMaterials = materials.filter(
            material => !selectedIds.includes(material?.id),
          );
          if (removedMaterials.length > 0) {
            try {
              const result = await untag({
                variables: {
                  id,
                  materials: removedMaterials.map(material => ({
                    id: material.id,
                    type: MaterialType[material.__typename],
                  })),
                  query,
                  perPage,
                  page,
                  sortOrder,
                  pressroomId,
                },
              });
              const error = result.data?.tagUntagMaterials?.error;
              if (error) {
                showFailureToast(error?.message ?? t('common_general_error'));
                return;
              }
            } catch (_err) {
              showFailureToast(t('common_general_error'));
              return;
            }
          }
          onClose();
        },
      }}
      onClose={() => onClose()}
      filters={
        <>
          <TagNameInputWrapper>
            <Input
              label={t('category_management_edit_category_name_label')}
              id="tagName"
              error={tagNameError}
              onChange={e => setName(e.target.value)}
              value={name}
            />
          </TagNameInputWrapper>
          <StyledLabel>{t('category_management_edit_category_material_heading')}</StyledLabel>
          <Header>
            <InputWrapper>
              <Input
                id="search-materials"
                label={t('category_management_edit_category_material_search_label')}
                onChange={e => setSearchQuery(e.target.value)}
                value={searchQuery}
              />
              <Select
                label={t('publish_pr-edit_related-material-card_filter-title')}
                options={filters.map(filter => filterItem(filter ?? null))}
                onChange={v => {
                  setSelectedFilter(v);
                }}
                value={selectedFilter}
              />
            </InputWrapper>
          </Header>
        </>
      }
    >
      {tag ? (
        <>
          <StyledLabel>{t('category_management_edit_category_uses_category_heading')}</StyledLabel>
          {materials?.map(material => (
            <FlexItem key={material.id}>
              <MaterialHeader $checked={selectedIds.includes(material.id)}>
                <MaterialIcon type={MaterialType[material.__typename]} />
                {material.header || t('common_untitled')}
              </MaterialHeader>
              <Center>
                <Checkbox
                  checked={selectedIds.includes(material.id)}
                  onChange={e => {
                    if (e.target.checked) {
                      setSelectedIds(selectedIds => [...selectedIds, material.id]);
                    } else {
                      setSelectedIds(selectedIds => selectedIds.filter(x => x !== material.id));
                    }
                  }}
                />
              </Center>
            </FlexItem>
          ))}
          {materials?.length === 0 && (
            <FlexItem>{t('category_management_no-material-found')}</FlexItem>
          )}
        </>
      ) : null}
    </Dialog>
  );
};

export default EditTagModal;
